import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef
} from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import {
  canDeletePurchaseNote,
  countServicesByPurchaseNote,
  DeletePurchaseNoteDialogResult,
  DeletePurchaseNoteResult,
  formatUserMessageText,
  getPurchaseNoteMutationBlockedMessage,
  getServicePurchaseNoteId,
  PurchaseNoteService,
  resolvePurchaseNoteGraphqlUserMessage,
  shouldRefreshAfterPurchaseNoteError
} from 'src/entities/purchase-note';

export interface DeletePurchaseNoteDialogData {
  service: {
    nombreServicio?: string | null;
    providerSnapshot?: { nombre?: string | null };
    purchaseNoteId?: string | null;
    purchaseNote?: {
      purchaseNoteId?: string | null;
      purchaseNoteNumber?: string | number | null;
      purchaseNoteStatus?: string | null;
      reviewDate?: string | null;
    } | null;
  };
  dataAll?: any[];
  contextLabel?: string;
}

type ViewState = 'confirm' | 'loading' | 'error' | 'blocked';

@Component({
  selector: 'app-dialog-delete-np',
  standalone: true,
  templateUrl: './dialog-delete-np.component.html',
  styleUrls: ['./dialog-delete-np.component.css'],
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule
  ],
  host: { class: 'delete-order-host' }
})
export class DialogDeleteNpComponent implements OnInit {
  state: ViewState = 'confirm';
  errorTitle: string | null = null;
  errorMessage: string | null = null;
  errorRequiresRefresh = false;

  readonly purchaseNoteId: string | null;
  readonly purchaseNoteNumber: string | number | null | undefined;
  readonly serviceName: string;
  readonly providerName: string;
  readonly purchaseNoteStatus: string | null;
  readonly contextLabel: string | null;
  readonly affectedServicesCount: number;

  private deletedNote: DeletePurchaseNoteResult | null = null;

  constructor(
    private readonly dialogRef: MatDialogRef<
      DialogDeleteNpComponent,
      DeletePurchaseNoteDialogResult
    >,
    @Inject(MAT_DIALOG_DATA) public data: DeletePurchaseNoteDialogData,
    private readonly purchaseNoteService: PurchaseNoteService
  ) {
    this.purchaseNoteId = getServicePurchaseNoteId(
      data.service as {
        purchaseNoteId?: string | null;
        purchaseNote?: { purchaseNoteId?: string | null } | null;
      }
    );
    this.purchaseNoteNumber = data.service?.purchaseNote?.purchaseNoteNumber;
    this.serviceName = data.service?.nombreServicio?.trim() || 'Servicio';
    this.providerName = data.service?.providerSnapshot?.nombre?.trim() || '-';
    this.purchaseNoteStatus =
      data.service?.purchaseNote?.purchaseNoteStatus ?? null;
    this.contextLabel = data.contextLabel ?? null;

    const counted = countServicesByPurchaseNote(
      data.dataAll,
      this.purchaseNoteId,
      this.purchaseNoteNumber
    );
    this.affectedServicesCount = counted || 1;
  }

  ngOnInit(): void {
    if (!this.canDelete) {
      this.state = 'blocked';
    }
  }

  get canDelete(): boolean {
    return canDeletePurchaseNote(this.data.service);
  }

  get blockedMessage(): string {
    if (!this.purchaseNoteId) {
      return 'Este servicio no tiene una Orden de pedido asociada.';
    }

    return getPurchaseNoteMutationBlockedMessage(
      'eliminar',
      this.purchaseNoteStatus
    );
  }

  get statusBadgeClass(): string {
    const status = this.purchaseNoteStatus?.toUpperCase();
    if (status === 'CONFIRMADO') return 'os-badge os-badge--confirmado';
    if (status === 'EN_PROCESO') return 'os-badge os-badge--proceso';
    if (status === 'PROCESADO') return 'os-badge os-badge--procesado';
    return 'os-badge';
  }

  confirmDelete(): void {
    if (!this.canDelete || this.state === 'loading' || !this.purchaseNoteId) {
      return;
    }

    this.state = 'loading';
    this.errorTitle = null;
    this.errorMessage = null;
    this.errorRequiresRefresh = false;

    this.purchaseNoteService
      .deletePurchaseNote({ purchaseNoteId: this.purchaseNoteId })
      .subscribe({
        next: (note) => {
          this.deletedNote = note;
          this.close(true);
        },
        error: (error: unknown) => {
          const msg = resolvePurchaseNoteGraphqlUserMessage(
            error,
            'No se pudo eliminar la Orden de pedido.'
          );
          this.errorTitle = msg.title;
          this.errorMessage = formatUserMessageText(msg);
          this.errorRequiresRefresh = shouldRefreshAfterPurchaseNoteError(error);
          this.state = 'error';
          if (this.errorRequiresRefresh) {
            this.dialogRef.disableClose = true;
          }
        }
      });
  }

  retry(): void {
    this.state = 'confirm';
    this.errorTitle = null;
    this.errorMessage = null;
    this.errorRequiresRefresh = false;
  }

  cancel(): void {
    this.close(false, false);
  }

  dismissBlocked(): void {
    this.close(false, false);
  }

  cerrarYActualizar(): void {
    this.dialogRef.disableClose = false;
    this.close(false, true);
  }

  private close(success: boolean, shouldRefresh = false): void {
    this.dialogRef.close({
      success,
      shouldRefresh,
      purchaseNoteId: this.purchaseNoteId,
      purchaseNoteNumber: this.purchaseNoteNumber,
      purchaseNote: success ? this.deletedNote : null
    });
  }
}
